import React from 'react';
import { Timeline } from './ui/timeline';

const timelineData = [
  {
    title: 'Discover',
    content: (
      <div className="soft-card timeline-step-card rounded-2xl border border-[--border-hover] p-4 md:p-5">
        <p className="text-[--text-secondary] text-sm md:text-base leading-relaxed mb-5">
          We map current workflows, identify bottlenecks, and prioritize high-impact opportunities
          with clear ROI and adoption feasibility.
        </p>
        <div className="timeline-step-media timeline-step-media--discover mb-4">
          <div className="timeline-step-meta">
            <span className="timeline-step-pill">Current-State Scan</span>
            <span className="timeline-step-pill">ROI Heatmap</span>
          </div>
          <p className="timeline-step-note">12 process lanes mapped in week one.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            Workflow Audit
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            ROI Prioritization
          </span>
        </div>
      </div>
    ),
  },
  {
    title: 'Design',
    content: (
      <div className="soft-card timeline-step-card rounded-2xl border border-[--border-hover] p-4 md:p-5">
        <p className="text-[--text-secondary] text-sm md:text-base leading-relaxed mb-5">
          We design solution architecture, control points, and governance paths so every use case
          is production-ready before implementation starts.
        </p>
        <div className="timeline-step-media timeline-step-media--design mb-4">
          <div className="timeline-step-meta">
            <span className="timeline-step-pill">System Blueprint</span>
            <span className="timeline-step-pill">Risk Controls</span>
          </div>
          <p className="timeline-step-note">Reference architecture finalized before build.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            System Blueprint
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            Governance Controls
          </span>
        </div>
      </div>
    ),
  },
  {
    title: 'Deploy',
    content: (
      <div className="soft-card timeline-step-card rounded-2xl border border-[--border-hover] p-4 md:p-5">
        <p className="text-[--text-secondary] text-sm md:text-base leading-relaxed mb-5">
          We integrate into your stack, launch in phased environments, and enable teams with clear
          handoff, observability, and ownership.
        </p>
        <div className="timeline-step-media timeline-step-media--deploy mb-4">
          <div className="timeline-step-meta">
            <span className="timeline-step-pill">Stack Integration</span>
            <span className="timeline-step-pill">Pilot Release</span>
          </div>
          <p className="timeline-step-note">First production lane shipped with observability.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            System Integration
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            Phased Rollout
          </span>
        </div>
      </div>
    ),
  },
  {
    title: 'Scale',
    content: (
      <div className="soft-card timeline-step-card rounded-2xl border border-[--border-hover] p-4 md:p-5">
        <p className="text-[--text-secondary] text-sm md:text-base leading-relaxed mb-5">
          Post-launch, we track outcome metrics, optimize workflows, and expand proven lanes across
          teams to drive sustained enterprise value.
        </p>
        <div className="timeline-step-media timeline-step-media--scale mb-4">
          <div className="timeline-step-meta">
            <span className="timeline-step-pill">Outcome Metrics</span>
            <span className="timeline-step-pill">Optimization Loop</span>
          </div>
          <p className="timeline-step-note">Expansion plan based on live performance signals.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            Outcome Metrics
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-md border border-[--border-hover] bg-[--bg-secondary]/70 text-[--text-secondary]">
            Continuous Optimization
          </span>
        </div>
      </div>
    ),
  },
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="relative pt-10 sm:pt-14 lg:pt-16 pb-24 md:pb-28 bg-[--bg-primary]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div className="mb-14 max-w-4xl">
          <p className="section-eyebrow mb-4">How It Works</p>
          <h2 className="text-[clamp(2rem,4.1vw,3.8rem)] font-semibold text-[--text-primary] leading-[1.02]">
            Drive ROI in 4 weeks:
            <br />
            <span className="accent-wash">Easy to set up, flexible to deploy</span>
          </h2>
          <p className="mt-6 text-lg text-[--text-secondary] max-w-3xl">
            Map what's broken. Build what fixes it. Deploy it. Keep it running. Clear milestones at every stage.
          </p>
        </div>

        <Timeline
          heading="Execution timeline across four delivery phases"
          description="Each phase ships tangible outputs so your teams move from roadmap to production without losing momentum."
          data={timelineData}
        />
      </div>
    </section>
  );
};

export default HowItWorks;
